import {
    ISignalsSelectionTypes,
    SignalDataEndpoint,
} from "components/Signals/SignalsModels";
import {
    CapabilityFlags,
    EndpointBehaviorConfig,
    GridBuilder,
    GridConfig,
    PaginationConfig,
    RenderOptions,
    RequestPatch,
    GroupingConfig,
    ExportConfig,
    PointSelectActions,
} from "./Types";
import { Palettes } from "components/UI/Visualization/GraphColorSchemes";
import theme from "Theme/AppTheme";

export const defaultSelectionType: ISignalsSelectionTypes = undefined;

export const defaultRequestPatch: RequestPatch = (state) => state;

export const defaultGridBuilder: GridBuilder = () => undefined;

export const defaultCapabilities: CapabilityFlags = {
    comparePrev: false,
    renderModes: ["#", "%"],
    spotlight: true,
    kboAnalyze: false,
    sqlView: false,
    pngExport: true,
    csvExport: "none",
    canSelectAllModules: false,
    pointSelectMenu: true,
    objectFilters: true,
};

export const defaultPagination: PaginationConfig = { paginated: false };

export const defaultRenderOptions: RenderOptions = {
    palette: Palettes.signalsChartColorScheme,
    comparePrevPalette: [
        theme.palette.secondary.main,
        theme.palette.primary[800],
    ],
    forceStacked: false,
    legendVisibility: "auto",
    isTemporal: false,
    patientJourneyBadge: false,
    menu: true,
    showAccuracy: false,
    emptyStateVariant: "empty",
    preferSeriesInTooltip: true,
    dataGridNumVisibleRows: 5,
    cacheKeyStrategy: "filtersOrSelectionsChange",
    tooltipTitleMetadataKey: undefined,
    eligibleConversationsTotalField: "y_totalcount",
    showScoredConversationCount: false,
};

export const defaultGroupingConfig: GroupingConfig = {
    // Grouping is opt-in per endpoint so existing charts do not accidentally
    // start emitting grouping request fields.
    enabled: false,
    ignoreGroupingOrderRule: false,
    disabledGroupByNames: [],
};

export const defaultExportConfig: ExportConfig = {
    allowColumnSelection: false,
};

export const defaultGridConfig: GridConfig = {
    gridColumnType: "Topics",
    dataGridBuilder: defaultGridBuilder,
    followPagination: false,
    selection: undefined,
};

export const defaultDrilldownActions: PointSelectActions = {
    askKbo: true,
    viewInConvos: true,
    viewTopics: false,
    viewDrivers: false,
    viewData: true,
    viewLevel: false,
};

export const defaultEndpointBehavior: EndpointBehaviorConfig = {
    requestPatch: defaultRequestPatch,
    transforms: [],
    dataGrid: undefined,
    pagination: defaultPagination,
    capabilities: defaultCapabilities,
    renderOptions: defaultRenderOptions,
    grouping: defaultGroupingConfig,
    exportConfig: defaultExportConfig,
    pointSelectActions: defaultDrilldownActions,
    pointSelectActionsByZoomLevel: {},
    moduleSelector: undefined,
    skillGroupingSelector: undefined,
    percentRemainderSeries: false,
    status: undefined,
    pendo: undefined,
    drilldown: undefined,
    stackOrderRuleIdentifier: undefined,
    reorder: undefined,
    kboModelOptions: undefined,
};

type EndpointBehaviorConfigOverrides = Partial<
    Omit<
        EndpointBehaviorConfig,
        | "pagination"
        | "capabilities"
        | "renderOptions"
        | "pointSelectActions"
        | "pointSelectActionsByZoomLevel"
        | "grouping"
        | "exportConfig"
    >
> & {
    pagination?: Partial<PaginationConfig>;
    capabilities?: Partial<CapabilityFlags>;
    renderOptions?: Partial<RenderOptions>;
    grouping?: Partial<GroupingConfig>;
    exportConfig?: Partial<ExportConfig>;
    pointSelectActions?: Partial<PointSelectActions>;
    pointSelectActionsByZoomLevel?:
        EndpointBehaviorConfig["pointSelectActionsByZoomLevel"];
};

export type EndpointBehaviorConfigMap = Partial<
    Record<SignalDataEndpoint, EndpointBehaviorConfigOverrides>
>;

export const mergeWithDefaults = (
    overrides?: EndpointBehaviorConfigOverrides,
): EndpointBehaviorConfig => {
    const dataGridConfig = overrides?.dataGrid
        ? {
              ...defaultGridConfig,
              ...overrides.dataGrid,
          }
        : defaultEndpointBehavior.dataGrid;

    return {
        requestPatch:
            overrides?.requestPatch ?? defaultEndpointBehavior.requestPatch,
        transforms: overrides?.transforms ?? [
            ...defaultEndpointBehavior.transforms,
        ],
        dataGrid: dataGridConfig,
        pagination: {
            ...defaultEndpointBehavior.pagination,
            ...overrides?.pagination,
        },
        capabilities: {
            ...defaultEndpointBehavior.capabilities,
            ...overrides?.capabilities,
        },
        renderOptions: {
            ...defaultEndpointBehavior.renderOptions,
            ...overrides?.renderOptions,
        },
        dataAvailability: overrides?.dataAvailability,
        grouping: {
            ...defaultGroupingConfig,
            ...overrides?.grouping,
        },
        exportConfig: {
            ...defaultExportConfig,
            ...overrides?.exportConfig,
        },
        pointSelectActions: {
            ...defaultDrilldownActions,
            ...overrides?.pointSelectActions,
        },
        pointSelectActionsByZoomLevel: {
            ...defaultEndpointBehavior.pointSelectActionsByZoomLevel,
            ...overrides?.pointSelectActionsByZoomLevel,
        },
        moduleSelector:
            overrides?.moduleSelector ?? defaultEndpointBehavior.moduleSelector,
        skillGroupingSelector:
            overrides?.skillGroupingSelector ??
            defaultEndpointBehavior.skillGroupingSelector,
        percentRemainderSeries:
            overrides?.percentRemainderSeries ??
            defaultEndpointBehavior.percentRemainderSeries,
        status: overrides?.status ?? defaultEndpointBehavior.status,
        pendo: overrides?.pendo ?? defaultEndpointBehavior.pendo,
        drilldown: overrides?.drilldown ?? defaultEndpointBehavior.drilldown,
        kboContextSuffix: overrides?.kboContextSuffix,
        kboUseYPercentTotal: overrides?.kboUseYPercentTotal,
        stackOrderRuleIdentifier:
            overrides?.stackOrderRuleIdentifier ??
            defaultEndpointBehavior.stackOrderRuleIdentifier,
        reorder: overrides?.reorder ?? defaultEndpointBehavior.reorder,
        kboModelOptions:
            overrides?.kboModelOptions ??
            defaultEndpointBehavior.kboModelOptions,
    };
};
